import {
  agendaSettingsSchema,
  agendaTrashSchema,
  type AgendaTrash,
  type CreateAppointment,
  type SeriesScope,
  type UpdateAppointment,
  appointmentSchema,
  approvalRequestSchema,
  availabilityBlockSchema,
  contactCategoryCountsSchema,
  contactDetailSchema,
  contactListSchema,
  contactSchema,
  ownerFactSchema,
  reminderSchema,
  type AgendaSettings,
  type ApprovalAction,
  type ApprovalRequest,
  type Appointment,
  type AvailabilityBlock,
  type AvailabilityBlockInput,
  type Contact,
  type ContactCategoryCounts,
  type ContactDetail,
  type ContactList,
  type ContactOverride,
  type CreateContact,
  type CreateOwnerFact,
  type CreateReminder,
  type OwnerFact,
  type PersonaAnswers,
  type Reminder,
  type UpdateAgendaSettings,
  type UpdateContact,
  type UpdateOwnerFact,
} from '@secretario/shared';
import { z } from 'zod';
import { apiFetch } from './http.js';

// ── Contatos ─────────────────────────────────────────────────────────────
export async function getContacts(params: {
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}): Promise<ContactList> {
  const q = new URLSearchParams();
  if (params.category && params.category !== 'all') q.set('category', params.category);
  if (params.search) q.set('search', params.search);
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 60));
  q.set('sort', params.sort ?? 'lastInteractionAt');
  return contactListSchema.parse(await apiFetch<unknown>(`/v1/contacts?${q.toString()}`));
}

export async function getContactCategoryCounts(): Promise<ContactCategoryCounts> {
  return contactCategoryCountsSchema.parse(await apiFetch<unknown>('/v1/contacts/categories'));
}

export async function createContact(input: CreateContact): Promise<Contact> {
  return contactSchema.parse(
    await apiFetch<unknown>('/v1/contacts', { method: 'POST', body: input }),
  );
}

export async function importContacts(
  contacts: { displayName: string; phone: string }[],
): Promise<{ imported: number; skipped: number }> {
  return apiFetch<{ imported: number; skipped: number }>('/v1/contacts/import', {
    method: 'POST',
    body: { contacts },
  });
}

/** Chaves dos telefones que já estão nos contatos (a comparação é feita no aparelho). */
export async function ownPhoneKeys(): Promise<Set<string>> {
  const { keys } = await apiFetch<{ keys: string[] }>('/v1/contacts/phone-keys');
  return new Set(keys);
}

export async function getContactDetail(id: string): Promise<ContactDetail> {
  return contactDetailSchema.parse(await apiFetch<unknown>(`/v1/contacts/${id}/details`));
}

export async function updateContact(id: string, patch: UpdateContact): Promise<Contact> {
  return contactSchema.parse(
    await apiFetch<unknown>(`/v1/contacts/${id}`, { method: 'PATCH', body: patch }),
  );
}

export function saveContactOverride(id: string, override: ContactOverride): Promise<void> {
  return apiFetch<void>(`/v1/contacts/${id}/override`, { method: 'PUT', body: override });
}

export function deleteContactOverride(id: string): Promise<void> {
  return apiFetch<void>(`/v1/contacts/${id}/override`, { method: 'DELETE' });
}

// ── Agenda ───────────────────────────────────────────────────────────────
export async function getAppointments(fromISO: string, toISO: string): Promise<Appointment[]> {
  const q = new URLSearchParams({ from: fromISO, to: toISO });
  return z.array(appointmentSchema).parse(await apiFetch<unknown>(`/v1/agenda/appointments?${q}`));
}

/** Chave que impede criar dois quando o toque repete ou a rede tenta de novo. */
const onceKey = () => `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export async function createAppointment(input: CreateAppointment): Promise<Appointment> {
  return appointmentSchema.parse(
    await apiFetch<unknown>('/v1/agenda/appointments', {
      method: 'POST',
      body: { idempotencyKey: onceKey(), ...input },
    }),
  );
}

export async function updateAppointment(
  id: string,
  patch: UpdateAppointment,
): Promise<Appointment> {
  return appointmentSchema.parse(
    await apiFetch<unknown>(`/v1/agenda/appointments/${id}`, { method: 'PATCH', body: patch }),
  );
}

/** Manda para a lixeira. Numa série, `scope` diz se é só este ou este e os próximos. */
export function deleteAppointment(
  id: string,
  target: { scope?: SeriesScope; occurrenceStart?: string } = {},
): Promise<void> {
  const q = new URLSearchParams();
  if (target.scope) q.set('scope', target.scope);
  if (target.occurrenceStart) q.set('occurrenceStart', target.occurrenceStart);
  const query = q.toString();
  return apiFetch<void>(`/v1/agenda/appointments/${id}${query ? `?${query}` : ''}`, {
    method: 'DELETE',
  });
}

export async function restoreAppointment(
  id: string,
  occurrenceStart?: string,
): Promise<Appointment> {
  return appointmentSchema.parse(
    await apiFetch<unknown>(`/v1/agenda/appointments/${id}/restore`, {
      method: 'POST',
      body: occurrenceStart ? { occurrenceStart } : {},
    }),
  );
}

export async function getAgendaTrash(): Promise<AgendaTrash> {
  return agendaTrashSchema.parse(await apiFetch<unknown>('/v1/agenda/trash'));
}

export async function getAgendaSettings(): Promise<AgendaSettings> {
  return agendaSettingsSchema.parse(await apiFetch<unknown>('/v1/agenda/settings'));
}

export async function updateAgendaSettings(patch: UpdateAgendaSettings): Promise<AgendaSettings> {
  return agendaSettingsSchema.parse(
    await apiFetch<unknown>('/v1/agenda/settings', { method: 'PUT', body: patch }),
  );
}

export async function getAvailabilityBlocks(): Promise<AvailabilityBlock[]> {
  return z
    .array(availabilityBlockSchema)
    .parse(await apiFetch<unknown>('/v1/agenda/availability-blocks'));
}

export async function createAvailabilityBlock(
  input: AvailabilityBlockInput,
): Promise<AvailabilityBlock> {
  return availabilityBlockSchema.parse(
    await apiFetch<unknown>('/v1/agenda/availability-blocks', { method: 'POST', body: input }),
  );
}

export async function updateAvailabilityBlock(
  id: string,
  input: AvailabilityBlockInput,
): Promise<AvailabilityBlock> {
  return availabilityBlockSchema.parse(
    await apiFetch<unknown>(`/v1/agenda/availability-blocks/${id}`, { method: 'PUT', body: input }),
  );
}

export function deleteAvailabilityBlock(id: string): Promise<void> {
  return apiFetch<void>(`/v1/agenda/availability-blocks/${id}`, { method: 'DELETE' });
}

export async function getReminders(): Promise<Reminder[]> {
  return z.array(reminderSchema).parse(await apiFetch<unknown>('/v1/reminders'));
}

/** Conta ao servidor quais lembretes já têm alarme agendado neste aparelho. */
export async function armReminders(ids: string[]): Promise<void> {
  await apiFetch<unknown>('/v1/reminders/armed', { method: 'POST', body: { ids } });
}

export async function createReminder(input: CreateReminder): Promise<Reminder> {
  return reminderSchema.parse(
    await apiFetch<unknown>('/v1/reminders', { method: 'POST', body: input }),
  );
}

export function cancelReminder(id: string): Promise<void> {
  return apiFetch<void>(`/v1/reminders/${id}`, { method: 'DELETE' });
}

export async function restoreReminder(id: string): Promise<Reminder> {
  return reminderSchema.parse(
    await apiFetch<unknown>(`/v1/reminders/${id}/restore`, { method: 'POST', body: {} }),
  );
}

// ── Sobre você ───────────────────────────────────────────────────────────
export async function getOwnerFacts(): Promise<{ facts: OwnerFact[] }> {
  return z
    .object({ facts: z.array(ownerFactSchema) })
    .parse(await apiFetch<unknown>('/v1/owner-facts'));
}

export async function createOwnerFact(input: CreateOwnerFact): Promise<OwnerFact> {
  return ownerFactSchema.parse(
    await apiFetch<unknown>('/v1/owner-facts', { method: 'POST', body: input }),
  );
}

export async function updateOwnerFact(id: string, patch: UpdateOwnerFact): Promise<OwnerFact> {
  return ownerFactSchema.parse(
    await apiFetch<unknown>(`/v1/owner-facts/${id}`, { method: 'PATCH', body: patch }),
  );
}

export function deleteOwnerFact(id: string): Promise<void> {
  return apiFetch<void>(`/v1/owner-facts/${id}`, { method: 'DELETE' });
}

/** "É isso mesmo": confirma uma lembrança que o Secretário anotou conversando. */
export async function confirmOwnerFact(id: string): Promise<OwnerFact> {
  return ownerFactSchema.parse(
    await apiFetch<unknown>(`/v1/owner-facts/${id}/confirm`, { method: 'POST' }),
  );
}

// ── Aprovações ───────────────────────────────────────────────────────────
export async function getApprovals(status = 'aguardando'): Promise<ApprovalRequest[]> {
  return z
    .array(approvalRequestSchema)
    .parse(await apiFetch<unknown>(`/v1/approvals?status=${status}`));
}

export async function actOnApproval(
  id: string,
  action: ApprovalAction,
  instruction?: string,
): Promise<ApprovalRequest> {
  return approvalRequestSchema.parse(
    await apiFetch<unknown>(`/v1/approvals/${id}/act`, {
      method: 'POST',
      body: instruction ? { action, instruction } : { action },
    }),
  );
}

export function bulkActOnApprovals(
  ids: string[],
  action: ApprovalAction,
): Promise<{ updated: number }> {
  return apiFetch<{ updated: number }>('/v1/approvals/bulk', {
    method: 'POST',
    body: { ids, action },
  });
}

// ── Jeito único ──────────────────────────────────────────────────────────
export function compilePersona(answers: PersonaAnswers): Promise<{ personaPrompt: string }> {
  return apiFetch<{ personaPrompt: string }>('/v1/profile/persona', {
    method: 'POST',
    body: answers,
  });
}

export function previewPersona(personaPrompt: string, message: string): Promise<{ reply: string }> {
  return apiFetch<{ reply: string }>('/v1/profile/persona/preview', {
    method: 'POST',
    body: { personaPrompt, message },
  });
}

// ── Conta ────────────────────────────────────────────────────────────────
const accountSchema = z.object({
  user: z.object({
    id: z.uuid(),
    username: z.string(),
    displayName: z.string(),
    phoneHint: z.string().nullable(),
  }),
  device: z.object({ id: z.uuid(), publicId: z.uuid() }),
  devices: z.array(
    z.object({
      publicId: z.uuid(),
      platform: z.enum(['android', 'ios', 'web']),
      label: z.string(),
      lastSeenAt: z.iso.datetime(),
      createdAt: z.iso.datetime(),
    }),
  ),
});
export type Account = z.infer<typeof accountSchema>;

export async function getAccount(): Promise<Account> {
  return accountSchema.parse(await apiFetch<unknown>('/v1/auth/me'));
}

export function revokeDevice(publicId: string): Promise<void> {
  return apiFetch<void>(`/v1/auth/devices/${publicId}/sessions`, { method: 'DELETE' });
}

/** Com autenticador ligado, o servidor pede o código dele (ou um código reserva). */
export function deactivateAccount(totp?: string): Promise<void> {
  return apiFetch<void>('/v1/auth/account', {
    method: 'DELETE',
    ...(totp ? { body: { totp } } : {}),
  });
}

// ── Pessoas no Secretário ────────────────────────────────────────────────
export type FoundPerson = { username: string; displayName: string; contactId: string | null };

/** Acha alguém pelo @usuário ou pelo celular com DDD (para conversar e ver o perfil). */
export function findPerson(query: string): Promise<FoundPerson> {
  return apiFetch<FoundPerson>('/v1/people/find', { method: 'POST', body: { query } });
}

/** Salva nos contatos alguém que já usa o Secretário. */
export async function addPersonToContacts(username: string): Promise<Contact> {
  return contactSchema.parse(
    await apiFetch<unknown>('/v1/contacts/from-person', { method: 'POST', body: { username } }),
  );
}
